import {
  AnimatedFolderIcon,
  type DropTargetDropEvent,
  type DropTargetOptions,
  MenuItem,
  MenuSeparator,
  notify,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useTrashFolderToast } from '@affine/core/components/hooks/affine/use-trash-folder-toast';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { buildSidebarCreateActions } from '@affine/core/modules/app-sidebar/views/area-actions/create-actions';
import { AuthService } from '@affine/core/modules/cloud';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  createDefaultDocCreateConfig,
  type DocCreateConfig,
} from '@affine/core/modules/doc-share';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import {
  FolderApiService,
  folderCreateErrorI18nKey,
  folderMoveErrorI18nKey,
  FolderMutationOutboxService,
  type FolderPermissions,
  type FolderWithDetails,
  getRememberedFolderPermissions,
  moveFolderToTrashLocalFirst as moveFolderToTrashLocalFirstAction,
  OWNER_FOLDER_PERMISSIONS,
  rememberFolderPermissions,
} from '@affine/core/modules/folder-api';
import {
  createDefaultFolderCreateConfig,
  type FolderCreateConfig,
  type FolderPermissionDialogVariant,
  FolderShareModal,
} from '@affine/core/modules/folder-share';
import {
  NavigationPanelRevealService,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import {
  AccessibleFolderCacheStore,
  type AccessibleFolderMeta,
  FolderDocPrefetchService,
  type FolderNode,
  FolderStore,
  OrganizeService,
  WorkspacePresenceStore,
} from '@affine/core/modules/organize';
import {
  ObjectPermissionMutationService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { beginObjectConfigurationTrace } from '@affine/core/modules/permissions/utils/object-configuration-trace';
import { WorkspaceService } from '@affine/core/modules/workspace';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { Unreachable } from '@affine/env/constant';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  DeleteIcon,
  InvisibleIcon,
  LockIcon,
  RemoveFolderIcon,
  UnlinkIcon,
} from '@blocksuite/icons/rc';
import {
  LiveData,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { OrganizeCreateMenu } from '../../sections/organize/create-menu';
import {
  forEachNavigationPanelDragSelectionItem,
  getNavigationPanelDragSelection,
  NavigationPanelTreeNode,
  type NavigationPanelTreeNodeDropEffect,
  type NavigationPanelTreeSelectionItem,
} from '../../tree';
import type { NavigationPanelTreeNodeIcon } from '../../tree/node';
import { defineNavigationPanelNodeOperations } from '../../tree/operation-registry';
import { useNavigationPanelSidebarSortMode } from '../../tree/sidebar-sort-context';
import type { NodeOperation } from '../../tree/types';
import {
  compareSidebarManualOrderItems,
  sortSidebarItems,
} from '../../tree/use-sidebar-manual-order';
import { NavigationPanelCollectionNode } from '../collection';
import { NavigationPanelDocNode } from '../doc';
import * as presenceStyles from '../presence-glow.css';
import { NavigationPanelTagNode } from '../tag';
import type { GenericNavigationPanelNode } from '../types';
import { isFolderKnownEmpty } from './children-state';
import { FolderEmpty } from './empty';
import { ConfigureFolderMenuItem, FavoriteFolderOperation } from './operations';

type FolderNodeSidebarItem = {
  child: FolderNode;
  key: string;
  label: string;
  type?: string;
};

const TEAMSPACE_ROOT_SYSTEM_KIND = 'teamspace_root';
const FOLDER_NODE_OPERATION_IDS = {
  create: 'folder:create',
  favorite: 'folder:favorite',
  configure: 'folder:configure',
  trashSeparator: 'folder:trash-separator',
  trash: 'folder:trash',
} as const;

type FolderNodeOperationId =
  (typeof FOLDER_NODE_OPERATION_IDS)[keyof typeof FOLDER_NODE_OPERATION_IDS];

function isPrivateNavigationSource(data: {
  source: { data: AffineDNDData['draggable'] };
}) {
  return (
    data.source.data.from?.at === 'navigation-panel:private:folder-node' ||
    data.source.data.from?.at === 'navigation-panel:private:root-doc'
  );
}

function getPrivateFolderDragName(data: DropTargetDropEvent<AffineDNDData>) {
  const from = data.source.data.from;
  return from?.at === 'navigation-panel:private:folder-node'
    ? from.folderName
    : undefined;
}

function getDocPlacementSourceNodeId(data: {
  source: { data: AffineDNDData['draggable'] };
}) {
  const from = data.source.data.from;
  return from?.at === 'navigation-panel:organize:folder-node' ||
    from?.at === 'navigation-panel:private:folder-node'
    ? from.nodeId
    : null;
}

function isDocLikeNavigationEntity(entityType: string | undefined) {
  return (
    entityType === 'collection' || entityType === 'doc' || entityType === 'tag'
  );
}

export const NavigationPanelFolderNode = ({
  nodeId,
  onDrop,
  defaultRenaming,
  onRenamingChange,
  operations,
  location,
  dropEffect,
  canDrop,
  folderPermissionVariant,
  reorderable,
  parentPath,
  hiddenDocIds,
}: {
  defaultRenaming?: boolean;
  onRenamingChange?: (renaming: boolean) => void;
  folderPermissionVariant?: FolderPermissionDialogVariant;
  nodeId: string;
  hiddenDocIds?: ReadonlySet<string>;
  onDrop?: (data: DropTargetDropEvent<AffineDNDData>, node: FolderNode) => void;
  operations?:
    | NodeOperation[]
    | ((type: string, node: FolderNode) => NodeOperation[]);
} & Omit<GenericNavigationPanelNode, 'operations'>) => {
  const { organizeService } = useServices({
    OrganizeService,
  });
  const node = useLiveData(organizeService.folderTree.folderNode$(nodeId));
  const type = useLiveData(node?.type$);
  const data = useLiveData(node?.data$);
  const handleDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (!node) {
        return;
      }
      onDrop?.(data, node);
    },
    [node, onDrop]
  );
  const additionalOperations = useMemo(() => {
    if (!type || !node) {
      return;
    }
    if (typeof operations === 'function') {
      return operations(type, node);
    }
    return operations;
  }, [node, operations, type]);

  if (!node) {
    return;
  }

  if (type === 'folder') {
    return (
      <NavigationPanelFolderNodeFolder
        node={node}
        onDrop={handleDrop}
        defaultRenaming={defaultRenaming}
        onRenamingChange={onRenamingChange}
        operations={additionalOperations}
        dropEffect={dropEffect}
        reorderable={reorderable}
        canDrop={canDrop}
        folderPermissionVariant={folderPermissionVariant}
        location={location}
        parentPath={parentPath}
        hiddenDocIds={hiddenDocIds}
      />
    );
  } else if (type === 'doc') {
    return (
      data && (
        <NavigationPanelDocNode
          docId={data}
          hideLinkedChildren
          location={location}
          onDrop={handleDrop}
          reorderable={reorderable}
          canDrop={canDrop}
          dropEffect={dropEffect}
          operations={additionalOperations}
          parentPath={parentPath}
        />
      )
    );
  } else if (type === 'collection') {
    return (
      data && (
        <NavigationPanelCollectionNode
          collectionId={data}
          location={location}
          onDrop={handleDrop}
          canDrop={canDrop}
          reorderable={reorderable}
          dropEffect={dropEffect}
          operations={additionalOperations}
          parentPath={parentPath}
        />
      )
    );
  } else if (type === 'tag') {
    return (
      data && (
        <NavigationPanelTagNode
          tagId={data}
          location={location}
          onDrop={handleDrop}
          canDrop={canDrop}
          reorderable
          dropEffect={dropEffect}
          operations={additionalOperations}
          parentPath={parentPath}
        />
      )
    );
  }

  return;
};

// Define outside the `NavigationPanelFolderNodeFolder` to avoid re-render(the close animation won't play)
const NavigationPanelFolderIcon: NavigationPanelTreeNodeIcon = ({
  collapsed,
  className,
  draggedOver,
  treeInstruction,
}) => (
  <AnimatedFolderIcon
    className={className}
    open={
      !collapsed || (!!draggedOver && treeInstruction?.type === 'make-child')
    }
  />
);

const NavigationPanelLockedFolderIcon: NavigationPanelTreeNodeIcon = ({
  className,
}) => <LockIcon className={className} style={{ opacity: 0.5 }} />;

function createOptimisticFolderMeta({
  id,
  name,
  parentId,
  ownerUserId = null,
  visibility = 'public',
  inheritPermissions = true,
}: {
  id: string;
  name: string;
  parentId: string | null;
  ownerUserId?: string | null;
  visibility?: 'public' | 'private';
  inheritPermissions?: boolean;
}): AccessibleFolderMeta {
  return {
    id,
    name,
    parentId,
    ownerUserId,
    systemKind: null,
    visibility,
    inheritPermissions,
    isWorkspaceRoot: false,
    immutable: false,
    storageLimitBytes: null,
    storageUsedBytes: null,
    metadataOnly: false,
    docIds: [],
  };
}

function inferFolderPermissionVariant(
  folder: AccessibleFolderMeta | null,
  getCachedFolder: (folderId: string) => AccessibleFolderMeta | null
): FolderPermissionDialogVariant | undefined {
  if (!folder) {
    return undefined;
  }
  if (folder.systemKind === TEAMSPACE_ROOT_SYSTEM_KIND) {
    return 'teamspace-root';
  }

  const visited = new Set<string>();
  let parentId = folder.parentId;
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = getCachedFolder(parentId);
    if (!parent) {
      return undefined;
    }
    if (parent.systemKind === TEAMSPACE_ROOT_SYSTEM_KIND) {
      return 'teamspace-folder';
    }
    parentId = parent.parentId;
  }

  return undefined;
}

const OPTIMISTIC_OWNER_FOLDER_PERMISSIONS: FolderPermissions =
  OWNER_FOLDER_PERMISSIONS;

const NavigationPanelFolderNodeFolder = ({
  node,
  onDrop,
  defaultRenaming,
  onRenamingChange,
  location,
  operations: additionalOperations,
  canDrop,
  dropEffect,
  folderPermissionVariant,
  reorderable,
  parentPath,
  hiddenDocIds,
}: {
  defaultRenaming?: boolean;
  onRenamingChange?: (renaming: boolean) => void;
  folderPermissionVariant?: FolderPermissionDialogVariant;
  node: FolderNode;
  hiddenDocIds?: ReadonlySet<string>;
} & GenericNavigationPanelNode) => {
  const t = useI18n();
  const { workspaceService, featureFlagService, organizeService } = useServices(
    {
      WorkspaceService,
      CompatibleFavoriteItemsAdapter,
      FeatureFlagService,
      OrganizeService,
    }
  );
  const navigationPanelService = useService(NavigationPanelService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const sidebarSortMode = useNavigationPanelSidebarSortMode();
  const folderDocPrefetchService = useService(FolderDocPrefetchService);
  const folderStore = useService(FolderStore);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const permissionService = useService(WorkspacePermissionService);
  const authService = useService(AuthService);
  const presenceStore = useService(WorkspacePresenceStore);
  const workspaceId = workspaceService.workspace.id;
  const isOwnerOrAdmin = useLiveData(
    permissionService.permission.isOwnerOrAdmin$
  );
  const workspaceRole = useLiveData(permissionService.permission.role$);
  const account = useLiveData(authService.session.account$);
  const isCloudWorkspace = workspaceService.workspace.flavour !== 'local';
  const connectivityStatus = useConnectivityStatus();
  // sl-jmyl.2: Folder glow when docs inside have active viewers
  const glowingNodes = useLiveData(presenceStore.glowingNodeIds$);
  const isFolderGlowing = node.id ? glowingNodes.has(node.id) : false;
  const accessibleFolders$ = useMemo(
    () =>
      isCloudWorkspace
        ? LiveData.from(
            accessibleFolderCache.watchAllAccessibleFolders(),
            accessibleFolderCache.getCachedFolders()
          )
        : null,
    [accessibleFolderCache, isCloudWorkspace]
  );
  const accessibleFolders = useLiveData(accessibleFolders$);
  const accessibleFolderIdSet = useMemo(
    () => new Set((accessibleFolders ?? []).map(folder => folder.id)),
    [accessibleFolders]
  );
  const name = useLiveData(node.name$);
  const enableEmojiIcon = useLiveData(
    featureFlagService.flags.enable_emoji_folder_icon.$
  );
  const path = useMemo(
    () => [...(parentPath ?? []), `folder-${node.id}`],
    [parentPath, node.id]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => {
      navigationPanelService.setCollapsed(path, value);
    },
    [navigationPanelService, path]
  );
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [folderVisibility, setFolderVisibility] = useState<string | null>(null);
  const [folderInheritPermissions, setFolderInheritPermissions] = useState<
    boolean | null
  >(null);
  const [folderPermissions, setFolderPermissions] =
    useState<FolderPermissions | null>(null);
  const cachedFolder = node.id
    ? accessibleFolderCache.getCachedFolder(node.id)
    : null;
  const isMetadataOnly = cachedFolder?.metadataOnly ?? false;
  const localAuthorityFolderPermissions = node.id
    ? accessibleFolderCache.getLocalFolderPermissions(node.id)
    : null;
  const effectiveFolderPermissionVariant = useMemo(
    () =>
      folderPermissionVariant ??
      inferFolderPermissionVariant(cachedFolder, folderId =>
        accessibleFolderCache.getCachedFolder(folderId)
      ),
    [accessibleFolderCache, cachedFolder, folderPermissionVariant]
  );
  const isTeamspaceRootFolder =
    cachedFolder?.systemKind === TEAMSPACE_ROOT_SYSTEM_KIND;
  const cachedFolderOwnerUserId = cachedFolder?.ownerUserId ?? null;
  const cachedFolderVisibility = cachedFolder?.visibility ?? null;
  const cachedFolderInheritPermissions =
    cachedFolder?.inheritPermissions ?? null;
  // Mirrors backend FolderService: workspace owners/admins have full Folder_*
  // permissions. Workspace editors who own an optimistically-created folder can
  // use owner controls before the backend folder details round-trip completes.
  const canUseLocalOwnerPermissions =
    !isCloudWorkspace ||
    isOwnerOrAdmin === true ||
    (workspaceRole === Permission.Editor &&
      cachedFolderOwnerUserId === account?.id);
  const effectiveFolderPermissions = canUseLocalOwnerPermissions
    ? OPTIMISTIC_OWNER_FOLDER_PERMISSIONS
    : (folderPermissions ?? localAuthorityFolderPermissions);
  const canUpdateFolder = effectiveFolderPermissions?.Folder_Update === true;
  const canAddDocToFolder = effectiveFolderPermissions?.Folder_AddDoc === true;
  const canRemoveDocFromFolder =
    effectiveFolderPermissions?.Folder_RemoveDoc === true;
  const canCreateSubfolder =
    effectiveFolderPermissions?.Folder_CreateSubfolder === true;
  const canMoveFolder =
    effectiveFolderPermissions?.Folder_Move === true && !isTeamspaceRootFolder;
  const canTrashFolder =
    effectiveFolderPermissions?.Folder_Trash === true && !isTeamspaceRootFolder;
  const canReceiveDropAttempt = !isMetadataOnly;
  const canManualReorderChildren =
    !sidebarSortMode || sidebarSortMode === 'manual';
  const canWriteYjsFolders = !isCloudWorkspace || isOwnerOrAdmin === true;
  const canUseYjsFolderTrashRoute = !isCloudWorkspace;
  const folderDestructiveLifecycleOffline =
    isCloudWorkspace && !connectivityStatus.backendAvailable;

  // Fetch folder details from backend
  const folderApiService = useService(FolderApiService);
  const applyOptimisticFolderDetails = useCallback(() => {
    if (!node.id) {
      return false;
    }

    if (!canUseLocalOwnerPermissions) {
      return false;
    }

    if (cachedFolderVisibility !== null) {
      setFolderVisibility(cachedFolderVisibility);
      setFolderInheritPermissions(cachedFolderInheritPermissions);
    }
    setFolderPermissions(OPTIMISTIC_OWNER_FOLDER_PERMISSIONS);
    rememberFolderPermissions(
      workspaceId,
      node.id,
      OPTIMISTIC_OWNER_FOLDER_PERMISSIONS
    );
    return true;
  }, [
    cachedFolderInheritPermissions,
    cachedFolderVisibility,
    canUseLocalOwnerPermissions,
    node.id,
    workspaceId,
  ]);

  const applyFolderDetails = useCallback(
    (folder: FolderWithDetails | null) => {
      if (!folder && applyOptimisticFolderDetails()) {
        return;
      }
      const nextPermissions = canUseLocalOwnerPermissions
        ? OPTIMISTIC_OWNER_FOLDER_PERMISSIONS
        : (folder?.permissions ?? null);
      setFolderVisibility(folder?.visibility ?? null);
      setFolderInheritPermissions(folder?.inheritPermissions ?? null);
      setFolderPermissions(nextPermissions);
      if (node.id) {
        rememberFolderPermissions(workspaceId, node.id, nextPermissions);
      }
    },
    [
      applyOptimisticFolderDetails,
      canUseLocalOwnerPermissions,
      node.id,
      workspaceId,
    ]
  );

  const loadFolderDetails = useCallback(
    (fresh = false) => {
      const folderId = node.id;
      if (!folderId) return;
      const hasOptimisticDetails = applyOptimisticFolderDetails();
      if (
        hasOptimisticDetails &&
        cachedFolderVisibility !== null &&
        cachedFolderInheritPermissions !== null
      ) {
        return;
      }
      const request = fresh
        ? folderApiService.getFolder(workspaceId, folderId)
        : folderApiService.getFolderForNavigation(workspaceId, folderId);
      void request
        .then((folder: FolderWithDetails | null) => {
          if (!folder && hasOptimisticDetails) {
            return;
          }
          applyFolderDetails(folder);
        })
        .catch(error => {
          if (hasOptimisticDetails) {
            return;
          }
          if (isBackendUnavailableError(error)) {
            const rememberedPermissions = getRememberedFolderPermissions(
              workspaceId,
              folderId
            );
            if (rememberedPermissions) {
              setFolderPermissions(current => current ?? rememberedPermissions);
              return;
            }
          }
          setFolderVisibility(null);
          setFolderInheritPermissions(null);
          setFolderPermissions(null);
          rememberFolderPermissions(workspaceId, folderId, null);
        });
    },
    [
      applyOptimisticFolderDetails,
      applyFolderDetails,
      cachedFolderInheritPermissions,
      cachedFolderVisibility,
      folderApiService,
      node.id,
      workspaceId,
    ]
  );

  useEffect(() => {
    loadFolderDetails();
  }, [loadFolderDetails]);

  useEffect(() => {
    const folderId = node.id;
    if (!isCloudWorkspace || !folderId) return;
    const subscription =
      accessibleFolderCache.subscribeToFolderDetailsInvalidations(
        folderId,
        () => loadFolderDetails(true)
      );

    return () => subscription.unsubscribe();
  }, [accessibleFolderCache, isCloudWorkspace, loadFolderDetails, node.id]);

  const canConfigureFolder =
    effectiveFolderPermissions?.Folder_Users_Manage === true;
  const statusPostfix = useMemo(() => {
    const icons: React.ReactNode[] = [];
    if (folderVisibility === 'private') {
      icons.push(
        <InvisibleIcon
          key="private"
          style={{ width: 14, height: 14, opacity: 0.5, flexShrink: 0 }}
        />
      );
    }
    if (folderInheritPermissions === false) {
      icons.push(
        <UnlinkIcon
          key="no-inherit"
          style={{ width: 14, height: 14, opacity: 0.5, flexShrink: 0 }}
        />
      );
    }
    if (icons.length === 0) return null;
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {icons}
      </span>
    );
  }, [folderVisibility, folderInheritPermissions]);

  const { createPage } = usePageHelper(
    workspaceService.workspace.docCollection
  );
  const applyDocPermissions = useCallback(
    (docId: string, config: DocCreateConfig) => {
      void objectPermissionMutation
        .grantPendingAssignments({ id: docId, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply document permissions:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
    },
    [objectPermissionMutation, t]
  );

  const showFolderTrashToast = useTrashFolderToast();

  const notifyNoFolderMovePermission = useCallback(() => {
    notify.error({
      title:
        t[
          'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
        ](),
    });
  }, [t]);

  const notifyNoFolderSourcePermission = useCallback(() => {
    notify.error({
      title:
        t[
          'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-source-denied'
        ](),
    });
  }, [t]);

  const notifyFolderMoveCycle = useCallback(() => {
    notify.error({
      title:
        t[
          'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-cycle-denied'
        ](),
    });
  }, [t]);

  const notifyNoFolderWritePermission = useCallback(() => {
    notify.error({
      title:
        t[
          'com.streamlain.rootAppSidebar.organize.folder.error.no-write-permission'
        ](),
    });
  }, [t]);

  const notifyConnectionRequired = useCallback(() => {
    notify.error({
      title: t['com.streamlain.settings.connection-required.title'](),
      message: t['com.streamlain.settings.connection-required.description'](),
    });
  }, [t]);

  const canUseServerDestructiveLifecycle = useCallback(
    () => !isCloudWorkspace || connectivityStatus.backendAvailable,
    [connectivityStatus.backendAvailable, isCloudWorkspace]
  );

  const handleTrash = useCallback(() => {
    if (!node.id) return;
    if (!canTrashFolder) {
      notify.error({
        title:
          t[
            'com.streamlain.rootAppSidebar.organize.folder.error.no-trash-permission'
          ](),
      });
      return;
    }
    void moveFolderToTrashLocalFirstAction({
      accessibleDocCache,
      accessibleFolderCache,
      folderApiService,
      folderId: node.id,
      folderStore,
      canTrashFolder,
      canUseYjsFolderRoute: canUseYjsFolderTrashRoute,
      canUseServerDestructiveLifecycle,
      workspaceId,
      notifyConnectionRequired,
      notifyMoveFailed: () => {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.trash-failed'
            ](),
        });
      },
      notifyRestoreFailed: () => {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.restore-failed'
            ](),
        });
      },
      onTrashConfirmed: () => {
        track.$.navigationPanel.organize.deleteOrganizeItem({
          type: 'folder',
        });
      },
      showFolderTrashToast,
      logError: console.error,
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    canUseServerDestructiveLifecycle,
    canUseYjsFolderTrashRoute,
    canTrashFolder,
    folderApiService,
    folderStore,
    node.id,
    notifyConnectionRequired,
    showFolderTrashToast,
    t,
    workspaceId,
  ]);

  const children = useLiveData(node.sortedChildren$);
  const folderTree = organizeService.folderTree;
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const docTrashStateById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.trashed])),
    [accessibleDocs]
  );

  const moveFolderToNode = useCallback(
    (folderId: string, index: string) => {
      if (!node.id) return;

      if (!isCloudWorkspace) {
        node.moveHere(folderId, index);
        track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const previousInfo = folderStore.getYjsFolderNodeInfo(folderId);
      const folderName =
        accessibleFolderCache.getCachedFolder(folderId)?.name ??
        previousInfo?.data ??
        folderTree.folderNode$(folderId).value?.name$.value ??
        'Folder';
      try {
        if (canWriteYjsFolders) {
          if (previousInfo) {
            node.moveHere(folderId, index);
          } else {
            node.createFolder(folderName, index, folderId);
          }
        }
        accessibleFolderCache.optimisticMoveFolder(
          folderId,
          node.id,
          {
            name: folderName,
          },
          undefined,
          {
            name: folderName,
            ownerUserId: account?.id ?? null,
            visibility: 'public',
          }
        );
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        console.error('Failed to move folder locally:', error);
        notify.error({
          title: t[folderMoveErrorI18nKey(error)](),
        });
        return;
      }

      track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
      void folderMutationOutbox
        .enqueueMoveFolder(folderId, node.id)
        .catch(error => {
          if (canWriteYjsFolders && previousInfo) {
            try {
              const previousParent = previousInfo.parentId
                ? folderTree.folderNode$(previousInfo.parentId).value
                : folderTree.rootFolder;
              previousParent?.moveHere(folderId, previousInfo.index);
            } catch {
              accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
            }
          }
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          console.error('Failed to move folder:', error);
          notify.error({
            title: t[folderMoveErrorI18nKey(error)](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      canWriteYjsFolders,
      folderStore,
      folderMutationOutbox,
      folderTree,
      isCloudWorkspace,
      node,
      t,
    ]
  );

  const movePrivateEntityToFolder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, index: string) => {
      if (!node.id) {
        return;
      }
      const entity = data.source.data.entity;
      if (!entity) {
        return;
      }

      if (entity.type === 'folder') {
        const optimisticSnapshot =
          accessibleFolderCache.captureOptimisticSnapshot();
        const previousInfo = folderStore.getYjsFolderNodeInfo(entity.id);
        const folderName = getPrivateFolderDragName(data) ?? 'Folder';
        try {
          if (canWriteYjsFolders) {
            if (previousInfo) {
              node.moveHere(entity.id, index);
            } else {
              node.createFolder(folderName, index, entity.id);
            }
          }
          accessibleFolderCache.optimisticMoveFolder(
            entity.id,
            node.id,
            {
              name: folderName,
              visibility: 'public',
              systemKind: null,
            },
            undefined,
            {
              name: folderName,
              ownerUserId: account?.id ?? null,
              visibility: 'public',
              systemKind: null,
            }
          );
          setCollapsed(false);
        } catch (error) {
          console.error('Failed to move private folder into folder:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          notify.error({
            title: t[folderMoveErrorI18nKey(error)](),
          });
          return;
        }

        void folderMutationOutbox
          .enqueueMoveFolder(entity.id, node.id)
          .catch(error => {
            if (canWriteYjsFolders && previousInfo) {
              try {
                const previousParent = previousInfo.parentId
                  ? folderTree.folderNode$(previousInfo.parentId).value
                  : folderTree.rootFolder;
                previousParent?.moveHere(entity.id, previousInfo.index);
              } catch {
                accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
              }
            } else if (canWriteYjsFolders) {
              try {
                folderTree.folderNode$(entity.id).value?.delete();
              } catch {
                // Cache rollback below restores the private sidebar.
              }
            }
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            console.error('Failed to move private folder into folder:', error);
            notify.error({
              title: t[folderMoveErrorI18nKey(error)](),
            });
          });
        track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        return;
      }

      return;
    },
    [
      accessibleFolderCache,
      account?.id,
      canWriteYjsFolders,
      folderMutationOutbox,
      folderTree,
      folderStore,
      node,
      setCollapsed,
      t,
    ]
  );

  const resolveDocMoveSourceFolder = useCallback(
    (data: { source: { data: AffineDNDData['draggable'] } }) => {
      const entity = data.source.data.entity;
      if (entity?.type !== 'doc') {
        return { folderId: null, known: false };
      }
      const sourcePlacementNodeId = getDocPlacementSourceNodeId(data);
      const previousPlacement = sourcePlacementNodeId
        ? folderStore.getYjsDocLinkInfo(sourcePlacementNodeId)
        : null;
      if (previousPlacement?.parentId) {
        return { folderId: previousPlacement.parentId, known: true };
      }

      const cachedSourceFolder = accessibleFolderCache
        .getCachedFolders()
        .find(folder => folder.docIds.includes(entity.id));
      if (cachedSourceFolder) {
        return { folderId: cachedSourceFolder.id, known: true };
      }

      return { folderId: null, known: !sourcePlacementNodeId };
    },
    [accessibleFolderCache, folderStore]
  );

  const canRemoveDocFromMoveSource = useCallback(
    (data: { source: { data: AffineDNDData['draggable'] } }) => {
      const source = resolveDocMoveSourceFolder(data);
      if (!source.known) {
        return false;
      }
      if (!source.folderId || source.folderId === node.id) {
        return true;
      }
      if (!isCloudWorkspace || isOwnerOrAdmin === true) {
        return true;
      }

      const sourceFolder = accessibleFolderCache.getCachedFolder(
        source.folderId
      );
      if (sourceFolder?.systemKind === 'private_mail') {
        return false;
      }

      return (
        getRememberedFolderPermissions(workspaceId, source.folderId)
          ?.Folder_RemoveDoc === true
      );
    },
    [
      accessibleFolderCache,
      isCloudWorkspace,
      isOwnerOrAdmin,
      node.id,
      resolveDocMoveSourceFolder,
      workspaceId,
    ]
  );

  const canMoveDocIntoThisFolder = useCallback(
    (data: { source: { data: AffineDNDData['draggable'] } }) =>
      data.source.data.entity?.type === 'doc' &&
      canAddDocToFolder &&
      canRemoveDocFromMoveSource(data),
    [canAddDocToFolder, canRemoveDocFromMoveSource]
  );

  const isDocMoveSourcePermissionSettled = useCallback(
    (data: { source: { data: AffineDNDData['draggable'] } }) => {
      const source = resolveDocMoveSourceFolder(data);
      if (!source.known) {
        return false;
      }
      if (
        !source.folderId ||
        source.folderId === node.id ||
        isPrivateNavigationSource(data) ||
        !isCloudWorkspace ||
        isOwnerOrAdmin === true
      ) {
        return true;
      }
      const sourceFolder = accessibleFolderCache.getCachedFolder(
        source.folderId
      );
      if (sourceFolder?.systemKind === 'private_mail') {
        return true;
      }
      return (
        getRememberedFolderPermissions(workspaceId, source.folderId) !==
        undefined
      );
    },
    [
      accessibleFolderCache,
      isCloudWorkspace,
      isOwnerOrAdmin,
      node.id,
      resolveDocMoveSourceFolder,
      workspaceId,
    ]
  );

  const canMoveFolderFromSource = useCallback(
    (folderId: string) => {
      if (!isCloudWorkspace || isOwnerOrAdmin === true) {
        return true;
      }
      return (
        getRememberedFolderPermissions(workspaceId, folderId)?.Folder_Move ===
        true
      );
    },
    [isCloudWorkspace, isOwnerOrAdmin, workspaceId]
  );

  const isFolderMoveSourcePermissionSettled = useCallback(
    (folderId: string) =>
      !isCloudWorkspace ||
      isOwnerOrAdmin === true ||
      getRememberedFolderPermissions(workspaceId, folderId) !== undefined,
    [isCloudWorkspace, isOwnerOrAdmin, workspaceId]
  );

  const moveDocToFolder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, index: string) => {
      const entity = data.source.data.entity;
      if (!node.id || entity?.type !== 'doc') {
        return;
      }
      if (!canAddDocToFolder || !canRemoveDocFromMoveSource(data)) {
        notifyNoFolderWritePermission();
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const sourcePlacementNodeId = getDocPlacementSourceNodeId(data);
      const previousPlacement = sourcePlacementNodeId
        ? folderStore.getYjsDocLinkInfo(sourcePlacementNodeId)
        : null;
      let movedPlacementNodeId: string | null = null;

      try {
        accessibleFolderCache.optimisticAddDocToFolder(node.id, entity.id);
        if (!isCloudWorkspace || canUseLocalOwnerPermissions) {
          movedPlacementNodeId = folderStore.moveDocLinkOrCreate(
            node.id,
            entity.id,
            index,
            sourcePlacementNodeId
          );
        }
        setCollapsed(false);
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        console.error('Failed to move doc into folder locally:', error);
        notify.error({
          title:
            t[
              folderMoveErrorI18nKey(
                error,
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              )
            ](),
        });
        return;
      }

      if (!isCloudWorkspace) {
        return;
      }

      void folderMutationOutbox
        .enqueueAddDocToFolder(node.id, entity.id)
        .catch(error => {
          if (movedPlacementNodeId) {
            try {
              if (previousPlacement) {
                const previousParent = previousPlacement.parentId
                  ? folderTree.folderNode$(previousPlacement.parentId).value
                  : folderTree.rootFolder;
                previousParent?.moveHere(
                  movedPlacementNodeId,
                  previousPlacement.index
                );
              } else {
                folderTree.folderNode$(movedPlacementNodeId).value?.delete();
              }
            } catch {
              accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
            }
          }
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          console.error('Failed to move doc into folder:', error);
          notify.error({
            title:
              t[
                folderMoveErrorI18nKey(
                  error,
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                )
              ](),
          });
        });
    },
    [
      accessibleFolderCache,
      canAddDocToFolder,
      canRemoveDocFromMoveSource,
      canUseLocalOwnerPermissions,
      folderMutationOutbox,
      folderStore,
      folderTree,
      isCloudWorkspace,
      node.id,
      notifyNoFolderWritePermission,
      setCollapsed,
      t,
    ]
  );

  // For metadataOnly folders: show only doc children with direct grants
  const accessibleDocIds$ = useMemo(
    () =>
      isCloudWorkspace
        ? LiveData.from(accessibleDocCache.watchAccessibleDocIds(), [])
        : null,
    [isCloudWorkspace, accessibleDocCache]
  );
  const accessibleDocIds = useLiveData(accessibleDocIds$);
  const accessibleDocIdSet = useMemo(
    () => new Set(accessibleDocIds ?? []),
    [accessibleDocIds]
  );

  // Filter children for metadataOnly folders: only docs the user has direct access to
  const visibleChildren = useMemo(() => {
    return children.filter(child => {
      const info = child.info$.value;
      if (info?.type === 'folder') {
        return (
          !isMetadataOnly &&
          (!isCloudWorkspace ||
            (child.id !== null && accessibleFolderIdSet.has(child.id)))
        );
      }
      if (info?.type !== 'doc') {
        return false;
      }
      if (docTrashStateById.get(info.data) === true) {
        return false;
      }
      if (hiddenDocIds?.has(info.data)) {
        return false;
      }
      return !isMetadataOnly || accessibleDocIdSet.has(info.data);
    });
  }, [
    accessibleDocIdSet,
    accessibleFolderIdSet,
    children,
    docTrashStateById,
    hiddenDocIds,
    isCloudWorkspace,
    isMetadataOnly,
  ]);
  const visibleChildItems = useMemo<FolderNodeSidebarItem[]>(() => {
    return visibleChildren.map(child => {
      const info = child.info$.value;
      const type = info?.type;
      const infoData = info?.data ?? '';
      const label =
        type === 'doc' ? (docTitleById.get(infoData) ?? '') : infoData;
      return {
        child,
        key: child.id ?? info?.id ?? '',
        label,
        type,
      };
    });
  }, [docTitleById, visibleChildren]);
  const orderedVisibleChildren = useMemo(() => {
    if (!sidebarSortMode || sidebarSortMode === 'manual') {
      return visibleChildItems.map(item => item.child);
    }
    return sortSidebarItems(
      visibleChildItems,
      sidebarSortMode,
      compareSidebarManualOrderItems
    ).map(item => item.child);
  }, [sidebarSortMode, visibleChildItems]);
  const folderKnownEmpty = isFolderKnownEmpty(
    cachedFolder?.hasChildren,
    orderedVisibleChildren.length
  );

  useEffect(() => {
    if (
      !isCloudWorkspace ||
      !node.id ||
      collapsed !== false ||
      isMetadataOnly ||
      cachedFolder?.hasChildren !== true ||
      children.length > 0
    ) {
      return;
    }

    void accessibleFolderCache
      .fetchFolderChildren(node.id, { force: true })
      .catch(() => {});
  }, [
    accessibleFolderCache,
    cachedFolder?.hasChildren,
    children.length,
    collapsed,
    isCloudWorkspace,
    isMetadataOnly,
    node.id,
  ]);

  const hasVisibleChildren = isMetadataOnly && visibleChildren.length > 0;

  const dndData = useMemo(() => {
    if (!node.id) {
      throw new Unreachable();
    }
    return {
      draggable: {
        entity: {
          type: 'folder',
          id: node.id,
        },
        folderTrash: {
          canTrashFolder,
        },
        from: location,
      },
      dropTarget: {
        at: 'navigation-panel:organize:folder',
      },
    } satisfies AffineDNDData;
  }, [canTrashFolder, location, node.id]);
  const selectionLocationAt = location?.at;
  const selectionLocationNodeId =
    location?.at === 'navigation-panel:organize:folder-node' ||
    location?.at === 'navigation-panel:private:folder-node'
      ? location.nodeId
      : undefined;
  const selectionItem = useMemo<
    NavigationPanelTreeSelectionItem | undefined
  >(() => {
    if (
      !node.id ||
      (selectionLocationAt !== 'navigation-panel:organize:folder-node' &&
        selectionLocationAt !== 'navigation-panel:private:folder-node') ||
      !selectionLocationNodeId
    ) {
      return undefined;
    }
    return {
      entity: {
        type: 'folder',
        id: node.id,
      },
      folderTrash: {
        canTrashFolder,
      },
      from: {
        at: selectionLocationAt,
        nodeId: selectionLocationNodeId,
      },
      label: name,
    };
  }, [
    canTrashFolder,
    name,
    node.id,
    selectionLocationAt,
    selectionLocationNodeId,
  ]);

  const handleRename = useCallback(
    (newName: string) => {
      if (!canUpdateFolder) {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.no-write-permission'
            ](),
        });
        return;
      }
      if (!node.id) {
        return;
      }
      const folderId = node.id;
      const nextName = newName.trim();
      if (!nextName || nextName === name) {
        return;
      }

      if (!isCloudWorkspace) {
        node.rename(nextName);
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticRenameFolder(folderId, nextName);

      void folderMutationOutbox
        .enqueueRenameFolder(folderId, nextName)
        .catch(error => {
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          console.error('Failed to sync folder rename:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.rename-failed'
              ](),
          });
        });
    },
    [
      accessibleFolderCache,
      canUpdateFolder,
      folderMutationOutbox,
      isCloudWorkspace,
      name,
      node,
      t,
    ]
  );

  const handleDropOnFolder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleDropOnFolder(itemData);
        });
        return;
      }
      if (data.source.data.entity?.type) {
        track.$.navigationPanel.folders.drop({
          type: data.source.data.entity.type,
        });
      }
      if (data.treeInstruction?.type === 'make-child') {
        const entityType = data.source.data.entity?.type;
        if (isDocLikeNavigationEntity(entityType)) {
          if (entityType === 'doc') {
            moveDocToFolder(data, node.indexAt('before'));
          }
          return;
        }
        if (isPrivateNavigationSource(data)) {
          movePrivateEntityToFolder(data, node.indexAt('before'));
          return;
        }
        if (data.source.data.entity?.type === 'folder') {
          if (
            node.id === data.source.data.entity.id ||
            node.beChildOf(data.source.data.entity.id)
          ) {
            notifyFolderMoveCycle();
            return;
          }
          if (!canMoveFolderFromSource(data.source.data.entity.id)) {
            notifyNoFolderSourcePermission();
            return;
          }
          if (!canCreateSubfolder) {
            notifyNoFolderMovePermission();
            return;
          }
          moveFolderToNode(data.source.data.entity.id, node.indexAt('before'));
        }
      } else {
        if (!canManualReorderChildren) {
          return;
        }
        if (!canMoveFolder) {
          notifyNoFolderMovePermission();
          return;
        }
        onDrop?.(data);
      }
    },
    [
      canManualReorderChildren,
      canCreateSubfolder,
      canMoveFolder,
      canMoveFolderFromSource,
      moveDocToFolder,
      moveFolderToNode,
      movePrivateEntityToFolder,
      notifyFolderMoveCycle,
      notifyNoFolderMovePermission,
      notifyNoFolderSourcePermission,
      node,
      onDrop,
    ]
  );

  const handleDropEffect = useCallback<NavigationPanelTreeNodeDropEffect>(
    data => {
      if (data.treeInstruction?.type === 'make-child') {
        if (isDocLikeNavigationEntity(data.source.data.entity?.type)) {
          return canMoveDocIntoThisFolder(data) ? 'move' : undefined;
        }
        if (data.source.data.entity?.type === 'folder') {
          if (
            !canCreateSubfolder ||
            !canMoveFolderFromSource(data.source.data.entity.id)
          ) {
            return;
          }
          if (
            node.id === data.source.data.entity.id ||
            node.beChildOf(data.source.data.entity.id)
          ) {
            return;
          }
          return 'move';
        }
      } else {
        if (isDocLikeNavigationEntity(data.source.data.entity?.type)) {
          return;
        }
        return dropEffect?.(data);
      }
      return;
    },
    [
      canCreateSubfolder,
      canMoveDocIntoThisFolder,
      canMoveFolderFromSource,
      dropEffect,
      node,
    ]
  );

  const handleDropOnPlaceholder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleDropOnPlaceholder(itemData);
        });
        return;
      }
      if (data.source.data.entity?.type) {
        track.$.navigationPanel.folders.drop({
          type: data.source.data.entity.type,
        });
      }
      const entityType = data.source.data.entity?.type;
      if (isDocLikeNavigationEntity(entityType)) {
        if (entityType === 'doc') {
          moveDocToFolder(data, node.indexAt('before'));
        }
        return;
      }
      if (entityType === 'folder' && !canMoveFolder) {
        notifyNoFolderMovePermission();
        return;
      }
      if (isPrivateNavigationSource(data)) {
        movePrivateEntityToFolder(data, node.indexAt('before'));
        return;
      }
      if (data.source.data.entity?.type === 'folder') {
        if (
          node.id === data.source.data.entity.id ||
          node.beChildOf(data.source.data.entity.id)
        ) {
          return;
        }
        moveFolderToNode(data.source.data.entity.id, node.indexAt('before'));
      }
    },
    [
      canMoveFolder,
      moveDocToFolder,
      moveFolderToNode,
      movePrivateEntityToFolder,
      notifyNoFolderMovePermission,
      node,
    ]
  );

  const handleDropOnChildren = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, dropAtNode?: FolderNode) => {
      if (!dropAtNode || !dropAtNode.id) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleDropOnChildren(itemData, dropAtNode);
        });
        return;
      }
      if (data.source.data.entity?.type) {
        track.$.navigationPanel.folders.drop({
          type: data.source.data.entity.type,
        });
      }
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        if (!canManualReorderChildren) {
          return;
        }
        const at =
          data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
        const entityType = data.source.data.entity?.type;
        if (
          isDocLikeNavigationEntity(entityType) &&
          dropAtNode.type$.value === 'folder'
        ) {
          return;
        }
        if (entityType === 'folder' && !canMoveFolder) {
          notifyNoFolderMovePermission();
          return;
        }
        if (isDocLikeNavigationEntity(entityType)) {
          return;
        }
        if (isPrivateNavigationSource(data)) {
          movePrivateEntityToFolder(data, node.indexAt(at, dropAtNode.id));
          return;
        }
        if (data.source.data.entity?.type === 'folder') {
          if (
            node.id === data.source.data.entity.id ||
            node.beChildOf(data.source.data.entity.id)
          ) {
            return;
          }
          moveFolderToNode(
            data.source.data.entity.id,
            node.indexAt(at, dropAtNode.id)
          );
        }
      } else if (data.treeInstruction?.type === 'reparent') {
        if (!canManualReorderChildren) {
          return;
        }
        if (!canMoveFolder) {
          notifyNoFolderMovePermission();
          return;
        }
        const currentLevel = data.treeInstruction.currentLevel;
        const desiredLevel = data.treeInstruction.desiredLevel;
        if (currentLevel === desiredLevel + 1) {
          onDrop?.({
            ...data,
            treeInstruction: {
              type: 'reorder-below',
              currentLevel,
              indentPerLevel: data.treeInstruction.indentPerLevel,
            },
          });
          return;
        } else {
          onDrop?.({
            ...data,
            treeInstruction: {
              ...data.treeInstruction,
              currentLevel: currentLevel - 1,
            },
          });
        }
      }
    },
    [
      canManualReorderChildren,
      canMoveFolder,
      moveFolderToNode,
      movePrivateEntityToFolder,
      notifyNoFolderMovePermission,
      node,
      onDrop,
    ]
  );

  const handleDropEffectOnChildren =
    useCallback<NavigationPanelTreeNodeDropEffect>(
      data => {
        if (
          data.treeInstruction?.type === 'reorder-above' ||
          data.treeInstruction?.type === 'reorder-below'
        ) {
          if (!canManualReorderChildren) {
            return;
          }
          if (isDocLikeNavigationEntity(data.source.data.entity?.type)) {
            return;
          }
          if (data.source.data.entity?.type === 'folder') {
            if (!canMoveFolder) {
              return;
            }
            if (
              node.id === data.source.data.entity.id ||
              node.beChildOf(data.source.data.entity.id)
            ) {
              return;
            }
            return 'move';
          }
        } else if (data.treeInstruction?.type === 'reparent') {
          if (!canManualReorderChildren) {
            return;
          }
          const currentLevel = data.treeInstruction.currentLevel;
          const desiredLevel = data.treeInstruction.desiredLevel;
          if (currentLevel === desiredLevel + 1) {
            dropEffect?.({
              ...data,
              treeInstruction: {
                type: 'reorder-below',
                currentLevel,
                indentPerLevel: data.treeInstruction.indentPerLevel,
              },
            });
            return;
          } else {
            dropEffect?.({
              ...data,
              treeInstruction: {
                ...data.treeInstruction,
                currentLevel: currentLevel - 1,
              },
            });
          }
        }
        return;
      },
      [canManualReorderChildren, canMoveFolder, dropEffect, node]
    );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      const entityType = args.source.data.entity?.type;
      if (args.treeInstruction && args.treeInstruction?.type !== 'make-child') {
        if (!canManualReorderChildren) {
          return false;
        }
        if (isDocLikeNavigationEntity(entityType)) {
          return false;
        }
        if (!canMoveFolder) {
          return false;
        }
        return (
          (typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? true
        );
      }

      if (args.source.data.entity?.type === 'folder') {
        if (
          args.treeInstruction?.type === 'make-child' &&
          !isPrivateNavigationSource(args) &&
          (effectiveFolderPermissions === null ||
            !isFolderMoveSourcePermissionSettled(args.source.data.entity.id))
        ) {
          return false;
        }
        return true;
      } else if (isDocLikeNavigationEntity(entityType)) {
        if (
          entityType === 'doc' &&
          (effectiveFolderPermissions === null ||
            !isDocMoveSourcePermissionSettled(args))
        ) {
          return false;
        }
        return entityType === 'doc';
      }
      return false;
    },
    [
      canDrop,
      canManualReorderChildren,
      canMoveFolder,
      effectiveFolderPermissions,
      isDocMoveSourcePermissionSettled,
      isFolderMoveSourcePermissionSettled,
    ]
  );

  const handleChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      const entityType = args.source.data.entity?.type;
      if (
        !canManualReorderChildren &&
        args.treeInstruction?.type !== 'make-child'
      ) {
        return false;
      }

      if (args.source.data.entity?.type === 'folder') {
        if (
          node.id === args.source.data.entity.id ||
          node.beChildOf(args.source.data.entity.id)
        ) {
          return false;
        }
        return canMoveFolder;
      } else if (isDocLikeNavigationEntity(entityType)) {
        return false;
      }
      return false;
    },
    [canManualReorderChildren, canMoveFolder, node]
  );

  const handleNewDoc = useCallback(
    (config: DocCreateConfig) => {
      if (!node.id) {
        return;
      }
      if (!canAddDocToFolder) {
        notifyNoFolderWritePermission();
        return;
      }
      const newDoc = createPage(undefined, {
        show: false,
        targetFolderId: node.id,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(node.id, newDoc.id);
      if (!isCloudWorkspace) {
        node.createLink('doc', newDoc.id, node.indexAt('before'));
      }
      applyDocPermissions(newDoc.id, config);
      track.$.navigationPanel.folders.createDoc();
      track.$.navigationPanel.organize.createOrganizeItem({
        type: 'link',
        target: 'doc',
      });
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId,
      });
    },
    [
      accessibleFolderCache,
      applyDocPermissions,
      canAddDocToFolder,
      createPage,
      isCloudWorkspace,
      navigationPanelRevealService,
      node,
      notifyNoFolderWritePermission,
      workspaceId,
    ]
  );

  const handleCreateNewDoc = useCallback(() => {
    handleNewDoc(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleNewDoc, t]);

  const handleSubfolderCreated = useCallback(
    async (config: FolderCreateConfig) => {
      if (!canCreateSubfolder) {
        notifyNoFolderWritePermission();
        return;
      }
      try {
        const folderId = canWriteYjsFolders
          ? node.createFolder(config.name, node.indexAt('before'))
          : nanoid();
        setNewFolderId(folderId);
        accessibleFolderCache.optimisticUpsertFolder(
          createOptimisticFolderMeta({
            id: folderId,
            name: config.name,
            parentId: node.id ?? null,
            ownerUserId: account?.id ?? null,
            visibility: config.visibility,
            inheritPermissions: config.inheritPermissions,
          }),
          { preserveUntilServerAck: true }
        );
        track.$.navigationPanel.organize.createOrganizeItem({ type: 'folder' });
        setCollapsed(false);

        await folderMutationOutbox.enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: node.id ?? null,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        });

        await objectPermissionMutation.grantPendingAssignments(
          { id: folderId, kind: 'folder' },
          config
        );
      } catch (err) {
        console.error('Failed to create backend subfolder:', err);
        notify.error({
          title: t[folderCreateErrorI18nKey(err)](),
        });
      }
    },
    [
      accessibleFolderCache,
      account?.id,
      canCreateSubfolder,
      canWriteYjsFolders,
      folderMutationOutbox,
      node,
      objectPermissionMutation,
      notifyNoFolderWritePermission,
      setCollapsed,
      t,
    ]
  );

  const handleCreateSubfolder = useCallback(() => {
    void handleSubfolderCreated(
      createDefaultFolderCreateConfig(
        effectiveFolderPermissionVariant,
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [effectiveFolderPermissionVariant, handleSubfolderCreated, t]);

  const folderOperations = useMemo(() => {
    return defineNavigationPanelNodeOperations<FolderNodeOperationId>([
      ...(canAddDocToFolder || canCreateSubfolder
        ? [
            {
              id: FOLDER_NODE_OPERATION_IDS.create,
              index: 0,
              inline: true,
              view: (
                <OrganizeCreateMenu
                  actions={buildSidebarCreateActions({
                    document: canAddDocToFolder
                      ? {
                          label:
                            t[
                              'com.streamlain.rootAppSidebar.organize.create.document'
                            ](),
                          onSelect: handleCreateNewDoc,
                        }
                      : undefined,
                    folder: canCreateSubfolder
                      ? {
                          label:
                            t[
                              'com.streamlain.rootAppSidebar.organize.create.folder'
                            ](),
                          onSelect: handleCreateSubfolder,
                        }
                      : undefined,
                  })}
                  testId="folder-add-doc-button"
                  tooltip={t[
                    'com.streamlain.rootAppSidebar.organize.create.tooltip'
                  ]()}
                />
              ),
            },
          ]
        : []),
      {
        id: FOLDER_NODE_OPERATION_IDS.favorite,
        index: 200,
        view: node.id ? <FavoriteFolderOperation id={node.id} /> : null,
      },
      {
        id: FOLDER_NODE_OPERATION_IDS.configure,
        index: 201,
        view:
          node.id && canConfigureFolder ? (
            <ConfigureFolderMenuItem
              onConfigure={() => {
                beginObjectConfigurationTrace('folder');
                setShareModalOpen(true);
              }}
            />
          ) : null,
      },
      ...(canTrashFolder
        ? [
            {
              id: FOLDER_NODE_OPERATION_IDS.trashSeparator,
              index: 9999,
              view: <MenuSeparator key="menu-separator" />,
            },
            {
              id: FOLDER_NODE_OPERATION_IDS.trash,
              index: 10000,
              view: (
                <MenuItem
                  data-testid="folder-menu-trash"
                  type={'danger'}
                  prefixIcon={<DeleteIcon />}
                  aria-disabled={folderDestructiveLifecycleOffline}
                  style={
                    folderDestructiveLifecycleOffline
                      ? { opacity: 0.55 }
                      : undefined
                  }
                  onClick={handleTrash}
                >
                  {t['com.streamlain.moveToTrash.title']()}
                </MenuItem>
              ),
            },
          ]
        : []),
    ]);
  }, [
    canAddDocToFolder,
    canConfigureFolder,
    canCreateSubfolder,
    canTrashFolder,
    folderDestructiveLifecycleOffline,
    handleCreateNewDoc,
    handleCreateSubfolder,
    handleTrash,
    node,
    t,
  ]);

  const finalOperations = useMemo(() => {
    if (additionalOperations) {
      return [...additionalOperations, ...folderOperations];
    }
    return folderOperations;
  }, [additionalOperations, folderOperations]);

  const childrenOperations = useCallback(
    (type: string, node: FolderNode) => {
      if (!canRemoveDocFromFolder) {
        return [];
      }
      if (type === 'doc' || type === 'collection' || type === 'tag') {
        return [
          {
            index: 999,
            view: (
              <MenuItem
                type={'danger'}
                prefixIcon={<RemoveFolderIcon />}
                data-event-props="$.navigationPanel.organize.deleteOrganizeItem"
                data-event-args-type={node.type$.value}
                onClick={() => node.delete()}
              >
                {t[
                  'com.streamlain.rootAppSidebar.organize.delete-from-folder'
                ]()}
              </MenuItem>
            ),
          },
        ] satisfies NodeOperation[];
      }
      return [];
    },
    [canRemoveDocFromFolder, t]
  );

  const handleCollapsedChange = useCallback(
    (collapsed: boolean) => {
      if (isMetadataOnly && !hasVisibleChildren) {
        if (!collapsed) {
          notify.warning({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder-no-access'
              ]?.() ??
              'Kein Zugriff auf Ordnerinhalte. Bitte den Administrator um Freigabe.',
          });
        }
        return;
      }
      if (collapsed) {
        setNewFolderId(null); // reset new folder id to clear the renaming state
        setCollapsed(true);
        if (node.id) {
          folderDocPrefetchService.onFolderCollapsed(node.id);
        }
      } else {
        setCollapsed(false);
        if (node.id) {
          void accessibleFolderCache
            .fetchFolderChildren(node.id)
            .catch(() => {});
          folderDocPrefetchService.onFolderExpanded(node.id);
        }
      }
    },
    [
      setCollapsed,
      isMetadataOnly,
      hasVisibleChildren,
      node.id,
      accessibleFolderCache,
      folderDocPrefetchService,
      t,
    ]
  );

  return (
    <>
      <NavigationPanelTreeNode
        icon={
          isMetadataOnly
            ? NavigationPanelLockedFolderIcon
            : NavigationPanelFolderIcon
        }
        iconClassName={isFolderGlowing ? presenceStyles.iconGlow : undefined}
        name={name}
        className={isFolderGlowing ? presenceStyles.presenceGlow : undefined}
        postfix={isMetadataOnly ? null : statusPostfix}
        dndData={isMetadataOnly ? undefined : dndData}
        selectionItem={isMetadataOnly ? undefined : selectionItem}
        onDrop={isMetadataOnly ? undefined : handleDropOnFolder}
        defaultRenaming={isMetadataOnly ? false : defaultRenaming}
        onRenamingChange={onRenamingChange}
        renameable={!isMetadataOnly && canUpdateFolder}
        extractEmojiAsIcon={!isMetadataOnly && enableEmojiIcon}
        reorderable={isMetadataOnly || !canMoveFolder ? false : reorderable}
        collapsed={isMetadataOnly && !hasVisibleChildren ? true : collapsed}
        setCollapsed={handleCollapsedChange}
        onRename={isMetadataOnly ? undefined : handleRename}
        operations={isMetadataOnly ? [] : finalOperations}
        revealOperations={!isMetadataOnly}
        canDrop={isMetadataOnly ? undefined : handleCanDrop}
        childrenEmpty={folderKnownEmpty}
        childrenPlaceholder={
          isMetadataOnly || !folderKnownEmpty ? undefined : (
            <FolderEmpty
              canDrop={canReceiveDropAttempt ? handleCanDrop : undefined}
              onDrop={
                canReceiveDropAttempt ? handleDropOnPlaceholder : undefined
              }
            />
          )
        }
        dropEffect={isMetadataOnly ? undefined : handleDropEffect}
        data-testid={`navigation-panel-folder-${node.id}`}
        data-folder-metadata-only={isMetadataOnly ? 'true' : 'false'}
        data-folder-permissions-settled={
          effectiveFolderPermissions !== null ? 'true' : 'false'
        }
        data-folder-can-add-doc={canAddDocToFolder ? 'true' : 'false'}
        data-folder-can-remove-doc={canRemoveDocFromFolder ? 'true' : 'false'}
        data-folder-can-create-subfolder={canCreateSubfolder ? 'true' : 'false'}
        data-folder-can-move={canMoveFolder ? 'true' : 'false'}
        explorerIconConfig={node.id ? { where: 'folder', id: node.id } : null}
      >
        {orderedVisibleChildren.map(child => (
          <NavigationPanelFolderNode
            key={child.id}
            nodeId={child.id as string}
            defaultRenaming={isMetadataOnly ? false : child.id === newFolderId}
            onRenamingChange={renaming => {
              if (!renaming && child.id === newFolderId) {
                setNewFolderId(null);
              }
            }}
            onDrop={isMetadataOnly ? undefined : handleDropOnChildren}
            operations={isMetadataOnly ? [] : childrenOperations}
            dropEffect={isMetadataOnly ? undefined : handleDropEffectOnChildren}
            canDrop={isMetadataOnly ? undefined : handleChildrenCanDrop}
            folderPermissionVariant={effectiveFolderPermissionVariant}
            location={{
              at: 'navigation-panel:organize:folder-node',
              nodeId: child.id as string,
            }}
            parentPath={path}
            hiddenDocIds={hiddenDocIds}
          />
        ))}
      </NavigationPanelTreeNode>
      {node.id && shareModalOpen && (
        <FolderShareModal
          folderId={node.id}
          variant={effectiveFolderPermissionVariant}
          open={shareModalOpen}
          onOpenChange={(open: boolean) => {
            setShareModalOpen(open);
            // Refresh folder details when modal closes (settings may have changed)
            if (!open && node.id) {
              folderApiService
                .getFolder(workspaceService.workspace.id, node.id)
                .then(applyFolderDetails)
                .catch(() => {
                  if (!applyOptimisticFolderDetails()) {
                    setFolderPermissions(null);
                  }
                });
            }
          }}
        />
      )}
    </>
  );
};
